// Lead detail per mockups/02-lead-detail.html: breadcrumb header with status
// pill + actions, follow-up banner (Done/+3d/+1w/Pick date), autosaving
// notes, timeline from the activity log, and a 304px rail of inline-editable
// fields. Convert modal per mockups/09-convert-lead.html.

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { parseProjectNumberDraft, projectNumberRequestError } from '../lib/projectNumbers';
import { melbourneDate, melbourneToday } from '../../shared/dates';
import {
  LEAD_ACQUISITION_LABELS,
  LEAD_OPEN_STATUSES,
  LEAD_REQUEST_TYPE_LABELS,
  LEAD_SOURCE_LABELS,
  LEAD_SOURCES,
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  LEAD_TYPES,
  LEAD_TYPE_LABELS,
  PHASE_LABELS,
  type Brand,
  type ClientWithCounts,
  type LeadAcquisition,
  type LeadRequestType,
  type LeadSource,
  type LeadStatus,
  type LeadType,
  type LeadWithNames,
  type ProjectPhase,
} from '../../shared/types';
import {
  addDays,
  CalendarPanel,
  DateField,
  Dropdown,
  formatDate,
  formatDayMonth,
  formatMoney,
  formatProjectNumber,
  formatWeekdayDate,
  Group,
  InlineDate,
  InlineMoney,
  InlineSelect,
  InlineText,
  Modal,
  NotesBlock,
  parseMoney,
  Pill,
  type PillTone,
  Seg,
  SelectField,
  Timeline,
  TimelineRow,
  TrashAction,
} from '../components/primitives';
import { grossFromEx } from '../../shared/finance';
import { CalendarCheckIcon, ClockIcon } from '../components/icons';
import { canLockInLead, lockedInStart } from '../lib/leadStage';

import { useIsMobile } from '../lib/useIsMobile';
import { LeadStageSheet, LeadStartDateModal } from '../components/leadStageSheet';
import { FieldRow, FieldSheet, type FieldKind } from '../components/FieldSheet';
import { PickerSheet } from '../components/PickerSheet';
import { DateSheet } from '../components/DateSheet';
import { PageMenu } from '../components/PageMenu';
import { DocumentSendsRail } from '../components/DocumentSendsRail';
import { DotsIcon } from '../components/icons';
import { MarketingStrategyField, type StrategyPick } from '../components/MarketingStrategyField';

interface ActivityEntry {
  id: number;
  kind: string;
  summary: string;
  created_at: number;
}

const NO_CLIENT_VALUE = '__no_client__';

const MOBILE_PICKER_TITLES = { source: 'Source', lead_type: 'Type', request_type: 'Request', acquisition: 'Acquisition' } as const;

const STATUS_DOTS: Record<LeadStatus, string> = {
  enquiry: 'var(--text-faint)',
  discovery_call: 'var(--accent-deep)',
  proposal_creation: 'var(--gold)',
  proposal_sent: 'var(--accent)',
  negotiation: 'var(--accent)',
  locked_in: 'var(--ok)',
  won: 'var(--ok)',
  lost: 'var(--text-faint)',
  ghosted: 'var(--text-faint)',
  postponed: 'var(--gold)',
  void: 'var(--text-faint)',
};

// Closed leads get an editable closed date (feedback round two §4). The rail
// label and the subtitle verb both follow the status; open leads show neither.
const CLOSED_DATE_LABELS: Partial<Record<LeadStatus, string>> = {
  won: 'Won date',
  lost: 'Lost date',
  void: 'Closed date',
};

const CLOSED_VERBS: Partial<Record<LeadStatus, string>> = {
  won: 'won',
  lost: 'lost',
  void: 'voided',
};

function statusTone(status: LeadStatus): PillTone {
  if (status === 'locked_in') return 'ok';
  if (LEAD_OPEN_STATUSES.includes(status) || status === 'won') return 'accent';
  if (status === 'postponed') return 'gold';
  return undefined;
}

/** Desktop status dropdown: moving into Locked in asks for the start date
    first (LeadStartDateModal saves both at once), like the board drag. */
export function selectDetailStatus(status: LeadStatus, current: LeadStatus,
  actions: { lock: () => void; patch: (fields: { status: LeadStatus }) => unknown }, project_id: string | null = null) {
  if (status === 'locked_in' && current !== 'locked_in') {
    if (canLockInLead({ project_id })) actions.lock();
  } else void actions.patch({ status });
}

/** "today — Thu 12 Jun" / "tomorrow — Fri 13 Jun" / "Wed 18 Jun" / "2d overdue — Tue 10 Jun" */
function fuWhen(iso: string, today: string): string {
  if (iso === today) return `today — ${formatWeekdayDate(iso)}`;
  if (iso === addDays(today, 1)) return `tomorrow — ${formatWeekdayDate(iso)}`;
  if (iso < today) {
    const days = Math.round((Date.parse(today) - Date.parse(iso)) / 86400000);
    return `${days}d overdue — ${formatWeekdayDate(iso)}`;
  }
  return formatWeekdayDate(iso);
}

// Request type and acquisition (round three §4). '' = not set; Outbound
// arrived with the Marketing tab (Cold Outreach leads).
const REQUEST_OPTIONS = [{ value: 'quote', label: 'Quote' }, { value: 'proposal', label: 'Proposal' }, { value: '', label: 'Not set' }] as const;
const ACQUISITION_OPTIONS = [{ value: 'paid', label: 'Paid' }, { value: 'organic', label: 'Organic' }, { value: 'outbound', label: 'Outbound' }, { value: '', label: 'Not set' }] as const;
type RequestChoice = (typeof REQUEST_OPTIONS)[number]['value'];
type AcquisitionChoice = (typeof ACQUISITION_OPTIONS)[number]['value'];

/** Banner copy for a Locked-in lead; `due` turns on the convert nudge. */
function lockedBanner(start: string | null, today: string): { text: string; due: boolean } {
  const s = lockedInStart(start, today);
  if (!start) return { text: 'Set a start date to get a convert nudge.', due: false };
  return s.due
    ? { text: `${s.text} — ${formatWeekdayDate(start)}. Convert it to a project.`, due: true }
    : { text: `Locked in · starts ${formatWeekdayDate(start)}`, due: false };
}

function tlDate(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()]}`;
}

export default function LeadDetail() {
  const isMobile = useIsMobile();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [mobileStage, setMobileStage] = useState(false);
  const [mobileTrash, setMobileTrash] = useState(false);
  const [trashBusy, setTrashBusy] = useState(false);
  const [mobileField, setMobileField] = useState<{ key: string; label: string; kind: FieldKind; value: string } | null>(null);
  const [mobileDate, setMobileDate] = useState<{ key: string; label: string; value: string | null } | null>(null);
  const [mobilePicker, setMobilePicker] = useState<'source' | 'lead_type' | 'request_type' | 'acquisition' | null>(null);
  const { id } = useParams<{ id: string }>();
  const [lead, setLead] = useState<LeadWithNames | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [missing, setMissing] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [clients, setClients] = useState<ClientWithCounts[] | null>(null);
  const [showConvert, setShowConvert] = useState(false);
  const navigate = useNavigate();
  const today = melbourneToday();
  const [fuPickOpen, setFuPickOpen] = useState(false);
  const [resurfacePickOpen, setResurfacePickOpen] = useState(false);
  const fuPickRef = useRef<HTMLSpanElement>(null);
  const resurfacePickRef = useRef<HTMLSpanElement>(null);

  const listSeq = useRef(0);
  const fetchLead = useCallback(() => {
    const seq = ++listSeq.current;
    api<{ lead: LeadWithNames; activity: ActivityEntry[] }>(`/api/admin/leads/${id}`)
      .then((d) => {
        if (seq !== listSeq.current) return;
        setMissing(false);
        setLead(d.lead);
        setActivity(d.activity);
      })
      .catch(() => { if (seq === listSeq.current) setMissing(true); });
  }, [id]);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchLead();
  }, [fetchLead]);
  useEffect(() => {
    fetchLead();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchLead, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      listSeq.current++;
    };
  }, [fetchLead]);

  const openClientPicker = () => {
    const next = !clientOpen;
    setClientOpen(next);
    if (!next || clients !== null) return;
    setClients([]);
    api<{ clients: ClientWithCounts[] }>('/api/admin/clients')
      .then((data) => setClients(data.clients))
      .catch((error) => {
        setClients(null);
        reportError(error, 'Could not load clients.');
      });
  };

  // Per-field PATCH, optimistic with rollback (design ledger).
  const patch = async (
    fields: Record<string, unknown>,
    optimisticFields: Record<string, unknown> = fields,
  ) => {
    if (!lead) return;
    const prev = lead;
    setLead({ ...lead, ...optimisticFields } as LeadWithNames);
    try {
      const res = await api<{ lead: LeadWithNames }>(`/api/admin/leads/${lead.id}`, {
        method: 'PATCH',
        json: fields,
      });
      setLead((l) => (l ? { ...l, ...res.lead } : l));
      refresh();
      return true;
    } catch (error) {
      setLead(prev);
      reportError(error, 'Could not update the lead.');
      return false;
    }
  };

  // Marketing strategy (round three #6): a pick is a manual attribution.
  const pickStrategy = (pick: StrategyPick) =>
    void patch({ strategy_id: pick.strategy_id }, { ...pick, attribution: pick.strategy_id ? 'manual' : null });

  if (missing) {
    return (
      <div className="page-body">
        <div className="content">
          <div className="empty-note">Lead not found.</div>
        </div>
      </div>
    );
  }
  if (!lead) return <div className="page-body" />;

  const isOpen = LEAD_OPEN_STATUSES.includes(lead.status);
  const locked = lead.status === 'locked_in' ? lockedBanner(lead.resurface_date, today) : null;
  const subBits = [
    lead.company ?? lead.contact_name,
    lead.enquiry_date
      ? `enquired ${formatDayMonth(lead.enquiry_date)}${lead.source ? ` via ${LEAD_SOURCE_LABELS[lead.source].toLowerCase()}` : ''}`
      : null,
  ].filter(Boolean);
  const closedBit = lead.closed_date && CLOSED_VERBS[lead.status]
    ? `${CLOSED_VERBS[lead.status]} ${formatDayMonth(lead.closed_date)}`
    : null;

  if (isMobile) {
    const edit = (key: string, label: string, value: string | null, kind: FieldKind = 'text') => setMobileField({ key, label, value: value ?? '', kind });
    const date = (key: string, label: string, value: string | null) => setMobileDate({ key, label, value });
    const textRow = (key: string, label: string, value: string | null, kind: FieldKind = 'text') =>
      <FieldRow key={key} label={label} value={value} empty={!value} mono={kind !== 'text'} tone={['email', 'tel', 'url'].includes(kind) ? 'acc' : undefined} onPress={() => edit(key, label, value, kind)} />;
    const postpone = async () => {
      if (await patch({ status: 'postponed' })) date('resurface_date', 'Resurface date', lead.resurface_date);
    };
    const money = () => edit('value_cents', 'Estimated value', lead.value_cents === null ? '' : String(lead.value_cents / 100), 'money');
    return <>
      <div className="page-body lead-mobile-detail">
        <header className="lead-detail-head">
          <div className="lead-detail-nav"><Link to="/leads">‹ Leads</Link><button className="ib" aria-label="More lead actions" onClick={() => setMobileMenu(true)}><DotsIcon /></button></div>
          <h1>{lead.name}</h1>
          <div className="lead-detail-sub">{subBits.join(' · ')}{closedBit && <span style={{ color: lead.status === 'won' ? 'var(--ok)' : undefined }}> · {closedBit}</span>}</div>
          <div className="lead-detail-pills"><button onClick={() => setMobileStage(true)}><Pill tone={statusTone(lead.status)}>{LEAD_STATUS_LABELS[lead.status]} ▾</Pill></button><button onClick={money}><Pill tone="gold">{formatMoney(lead.value_cents)} ex GST</Pill></button></div>
        </header>
        {isOpen && lead.followup_date && <div className={`lead-banner${lead.followup_date < today ? ' dng' : ''}`}>
          <div className="lead-banner-label">{lead.followup_date < today ? 'Follow up due' : 'Follow up'}</div><div className="lead-banner-text">{fuWhen(lead.followup_date, today)}</div>
          <div className="lead-banner-chips"><button className="cbtn acc" onClick={() => void patch({ followup_date: null })}>✓ Done</button><button className="cbtn" onClick={() => void patch({ followup_date: addDays(today, 3) })}>+3d</button><button className="cbtn" onClick={() => void patch({ followup_date: addDays(today, 7) })}>+1w</button><button className="cbtn" onClick={() => date('followup_date', 'Follow up', lead.followup_date)}>Pick date</button></div>
        </div>}
        {locked && <div className={`lead-banner locked${locked.due ? ' due' : ''}`}>
          <div className="lead-banner-label">{locked.due ? 'Locked in · start date' : 'Locked in'}</div><div className="lead-banner-text">{locked.text}</div>
          <div className="lead-banner-chips">{locked.due && !lead.project_id && <button className="cbtn acc" onClick={() => setShowConvert(true)}>Convert to project →</button>}<button className="cbtn" onClick={() => date('resurface_date', 'Start date', lead.resurface_date)}>{lead.resurface_date ? 'Change date' : 'Set start date'}</button></div>
        </div>}
        {lead.status === 'postponed' && <div className="lead-banner warn">
          <div className="lead-banner-label">Not now</div><div className="lead-banner-text">{lead.resurface_date ? `Resurfaces ${fuWhen(lead.resurface_date, today)}` : 'No resurface date set'}</div>
          <div className="lead-banner-chips"><button className="cbtn" onClick={() => void patch({ resurface_date: null })}>Clear</button><button className="cbtn" onClick={() => void patch({ resurface_date: addDays(today, 7) })}>+1w</button><button className="cbtn" onClick={() => void patch({ resurface_date: addDays(today, 30) })}>+1mo</button><button className="cbtn" onClick={() => date('resurface_date', 'Resurface date', lead.resurface_date)}>Pick date</button></div>
        </div>}
        {(lead.project_id || isOpen || lead.status === 'postponed' || lead.status === 'locked_in') && <div className="lead-detail-primary"><button className="cbtn wide pri" onClick={() => lead.project_id ? navigate(`/projects/${lead.project_id}`) : setShowConvert(true)}>{lead.project_id ? 'View project →' : 'Convert to project →'}</button></div>}
        <div className="lead-detail-notes"><NotesBlock notes={lead.notes} onSave={(notes) => patch({ notes })} /></div>
        <div className="sec-l">Details</div>
        <div className="lead-detail-fields">
          <FieldRow label="Status" value={LEAD_STATUS_LABELS[lead.status]} onPress={() => setMobileStage(true)} />
          {lead.status === 'locked_in' && <FieldRow label="Start date" value={lead.resurface_date ? formatWeekdayDate(lead.resurface_date) : ''} empty={!lead.resurface_date} mono tone="acc" onPress={() => date('resurface_date', 'Start date', lead.resurface_date)} />}
          <FieldRow label="Value · ex GST" value={formatMoney(lead.value_cents)} empty={lead.value_cents === null} mono tone="gold" onPress={money} />
          <FieldRow label="Follow up" value={lead.followup_date ? formatWeekdayDate(lead.followup_date) : ''} empty={!lead.followup_date} mono tone={isOpen && lead.followup_date && lead.followup_date < today ? 'danger' : undefined} onPress={() => date('followup_date', 'Follow up', lead.followup_date)} />
          {textRow('company', 'Company', lead.company)}
          {textRow('contact_name', 'Contact', lead.contact_name)}
          {textRow('contact_email', 'Email', lead.contact_email, 'email')}
          {textRow('contact_phone', 'Phone', lead.contact_phone, 'tel')}
          {textRow('website', 'Website', lead.website, 'url')}
          {textRow('address', 'Address', lead.address)}
          {textRow('secondary_contact_name', 'Secondary contact', lead.secondary_contact_name)}
          {textRow('secondary_contact_email', 'Secondary email', lead.secondary_contact_email, 'email')}
          <FieldRow label="Source" value={lead.source ? LEAD_SOURCE_LABELS[lead.source] : ''} empty={!lead.source} onPress={() => setMobilePicker('source')} />
          <MarketingStrategyField lead={lead} onSave={pickStrategy} />
          <FieldRow label="Type" value={lead.lead_type ? LEAD_TYPE_LABELS[lead.lead_type] : ''} empty={!lead.lead_type} onPress={() => setMobilePicker('lead_type')} />
          <FieldRow label="Request" value={lead.request_type ? LEAD_REQUEST_TYPE_LABELS[lead.request_type] : ''} empty={!lead.request_type} onPress={() => setMobilePicker('request_type')} />
          <FieldRow label="Acquisition" value={lead.acquisition ? LEAD_ACQUISITION_LABELS[lead.acquisition] : ''} empty={!lead.acquisition} onPress={() => setMobilePicker('acquisition')} />
          {textRow('campaign', 'Campaign', lead.campaign)}
          <FieldRow label="Enquiry date" value={formatDate(lead.enquiry_date)} empty={!lead.enquiry_date} mono onPress={() => date('enquiry_date', 'Enquiry date', lead.enquiry_date)} />
          {CLOSED_DATE_LABELS[lead.status] && <FieldRow label={CLOSED_DATE_LABELS[lead.status]!} value={formatDate(lead.closed_date)} empty={!lead.closed_date} mono onPress={() => date('closed_date', CLOSED_DATE_LABELS[lead.status]!, lead.closed_date)} />}
          <FieldRow label="Linked client" value={lead.client_name ? `${lead.client_name} ↗` : ''} empty={!lead.client_id} tone="acc" onPress={openClientPicker} />
          {lead.project_id && <FieldRow label="Converted to" value={`${lead.converted_project_number !== null ? formatProjectNumber(lead.converted_project_number) + ' ' : ''}${lead.converted_project_name ?? lead.name} →`} tone="acc" onPress={() => navigate(`/projects/${lead.project_id}`)} />}
        </div>
        <div className="sec-l">Proposals & quotes</div>
        <div style={{ padding: '0 20px' }}>
          <DocumentSendsRail filter={{ lead_id: lead.id }} defaults={{ lead_id: lead.id, client_id: lead.client_id ?? undefined, kind: 'quote' }} />
        </div>
        <div className="lead-detail-timeline"><Group title="Timeline"><Timeline>
          {activity.map(a => <TimelineRow key={a.id} date={formatDayMonth(melbourneDate(a.created_at))}>{a.summary}</TimelineRow>)}
          <TimelineRow date={formatDayMonth(melbourneDate(lead.created_at))}>Lead created{lead.source ? ` from ${LEAD_SOURCE_LABELS[lead.source]}` : ''}</TimelineRow>
        </Timeline></Group></div>
        <div className="lead-detail-stamp">CREATED {formatDate(melbourneDate(lead.created_at)).toUpperCase()}<br />UPDATED {formatDate(melbourneDate(lead.updated_at)).toUpperCase()}</div>
      </div>
      <PageMenu open={mobileMenu} title={lead.name} onClose={() => setMobileMenu(false)} items={[
        ...(!lead.project_id ? [{ key: 'convert', label: 'Convert to project', onSelect: () => setShowConvert(true) }] : []),
        ...(isOpen || lead.status === 'postponed' ? [
          { key: 'postpone', label: 'Postpone', sublabel: 'Set a resurface date', onSelect: () => void postpone() },
          { key: 'lost', label: 'Mark lost', onSelect: () => void patch({ status: 'lost', closed_date: melbourneToday() }) },
        ] : []),
        { key: 'trash', label: 'Move lead to trash', tone: 'danger', onSelect: () => setMobileTrash(true) },
      ]} />
      {mobileTrash && <Modal mobile="half" title="Move lead to trash?" context={lead.name} onClose={() => { if (!trashBusy) setMobileTrash(false); }} foot={<>
        <button className="btn" disabled={trashBusy} onClick={() => setMobileTrash(false)}>Cancel</button>
        <button className="btn danger" disabled={trashBusy} onClick={async () => {
          setTrashBusy(true);
          try { await api(`/api/admin/leads/${lead.id}`, { method: 'DELETE' }); navigate('/leads'); }
          catch (error) { reportError(error, 'Could not move the lead to trash.'); setTrashBusy(false); }
        }}>Move to trash</button></>}><p>You can restore this lead from Trash.</p></Modal>}
      {mobileStage && <LeadStageSheet lead={lead} onClose={() => setMobileStage(false)} onChanged={refresh} onConvert={() => setShowConvert(true)} />}
      {mobileField && <FieldSheet open title={mobileField.label} context={mobileField.kind === 'money' ? 'ex GST' : lead.name} kind={mobileField.kind} value={mobileField.value} onSave={raw => void patch({ [mobileField.key]: mobileField.kind === 'money' ? parseMoney(raw) : raw.trim() || null })} onClose={() => setMobileField(null)} />}
      {mobileDate && <DateSheet open title={mobileDate.label} context={lead.name} value={mobileDate.value} allowClear onPick={value => void patch({ [mobileDate.key]: value })} onClose={() => setMobileDate(null)} />}
      {mobilePicker && <PickerSheet open title={MOBILE_PICKER_TITLES[mobilePicker]} items={
        mobilePicker === 'source' ? [{ value: '', label: '— No source' }, ...LEAD_SOURCES.map(v => ({ value: v, label: LEAD_SOURCE_LABELS[v] }))]
          : mobilePicker === 'request_type' ? [{ value: '', label: '— Not set' }, ...REQUEST_OPTIONS.filter(o => o.value).map(o => ({ value: o.value, label: o.label }))]
          : mobilePicker === 'acquisition' ? [{ value: '', label: '— Not set' }, ...ACQUISITION_OPTIONS.filter(o => o.value).map(o => ({ value: o.value, label: o.label }))]
          : LEAD_TYPES.map(v => ({ value: v, label: LEAD_TYPE_LABELS[v] }))} value={lead[mobilePicker] ?? ''} onSelect={value => void patch({ [mobilePicker]: value || null })} onClose={() => setMobilePicker(null)} />}
      {clientOpen && <PickerSheet open title="Linked client" searchable items={[{ value: NO_CLIENT_VALUE, label: '— No client' }, ...(clients ?? []).map(c => ({ value: c.id, label: c.name }))]} value={lead.client_id ?? NO_CLIENT_VALUE} onSelect={value => void patch({ client_id: value === NO_CLIENT_VALUE ? null : value }, { client_id: value === NO_CLIENT_VALUE ? null : value, client_name: clients?.find(c => c.id === value)?.name ?? null })} onClose={() => setClientOpen(false)} />}
      {showConvert && <ConvertLeadModal lead={lead} onClose={() => setShowConvert(false)} onConverted={() => { setShowConvert(false); refresh(); }} />}
    </>;
  }

  return (
    <>
      <header className="detail-header">
        <Link className="crumb" to="/leads">
          Leads
        </Link>
        <span className="crumb-sep">/</span>
        <span className="crumb-current">{lead.name}</span>
        <span style={{ marginLeft: 6 }}>
          <Pill tone={statusTone(lead.status)}>{LEAD_STATUS_LABELS[lead.status]}</Pill>
        </span>
        <div className="detail-actions">
          {isOpen && (
            <button className="btn" onClick={() => patch({ status: 'lost' })}>
              Mark lost
            </button>
          )}
          {lead.project_id ? (
            <button className="btn primary" onClick={() => navigate(`/projects/${lead.project_id}`)}>
              View project →
            </button>
          ) : (
            <button className="btn primary" onClick={() => setShowConvert(true)}>
              Convert to project →
            </button>
          )}
        </div>
      </header>

      <div className="page-body">
        <div className="detail-grid">
          <div className="detail-main">
            <h1 className="detail-name">{lead.name}</h1>
            {(subBits.length > 0 || closedBit) && (
              <div className="detail-sub">
                {subBits.join(' · ')}
                {closedBit && (
                  <>
                    {subBits.length > 0 && ' · '}
                    <span style={lead.status === 'won' ? { color: 'var(--ok)' } : undefined}>{closedBit}</span>
                  </>
                )}
              </div>
            )}

            {locked && (
              <div className={`fu-banner locked${locked.due ? ' due' : ''}`}>
                <CalendarCheckIcon width={14} height={14} />
                <span>{locked.text}</span>
                <span className="snooze">
                  {locked.due && !lead.project_id && (
                    <button className="chip primary" onClick={() => setShowConvert(true)}>
                      Convert to project →
                    </button>
                  )}
                  <button className="chip" onClick={() => patch({ resurface_date: addDays(lead.resurface_date ?? today, 7) })}>
                    +1w
                  </button>
                </span>
              </div>
            )}

            {lead.followup_date && isOpen && (
              <div className="fu-banner">
                <ClockIcon width={14} height={14} style={{ stroke: 'var(--gold)', flexShrink: 0 }} />
                <span>
                  Follow up due <span className="when">{fuWhen(lead.followup_date, today)}</span>
                </span>
                <span className="snooze">
                  <button className="chip" onClick={() => patch({ followup_date: null })}>
                    Done
                  </button>
                  <button className="chip" onClick={() => patch({ followup_date: addDays(today, 3) })}>
                    +3d
                  </button>
                  <button className="chip" onClick={() => patch({ followup_date: addDays(today, 7) })}>
                    +1w
                  </button>
                  <span className="dropdown-anchor" ref={fuPickRef}>
                    <button className="chip" onClick={() => setFuPickOpen((o) => !o)}>
                      Pick date
                    </button>
                    {fuPickOpen && (
                      <CalendarPanel
                        value={lead.followup_date}
                        anchorRef={fuPickRef}
                        onPick={(iso) => iso && patch({ followup_date: iso })}
                        onClose={() => setFuPickOpen(false)}
                      />
                    )}
                  </span>
                </span>
              </div>
            )}

            {lead.status === 'postponed' && (
              <div className="fu-banner">
                <ClockIcon width={14} height={14} style={{ stroke: 'var(--gold)', flexShrink: 0 }} />
                <span>
                  {lead.resurface_date ? (
                    <>
                      Resurfaces <span className="when">{fuWhen(lead.resurface_date, today)}</span>
                    </>
                  ) : (
                    'No resurface date set'
                  )}
                </span>
                <span className="snooze">
                  <button className="chip" onClick={() => patch({ resurface_date: null })}>
                    Clear
                  </button>
                  <button className="chip" onClick={() => patch({ resurface_date: addDays(today, 7) })}>
                    +1w
                  </button>
                  <button className="chip" onClick={() => patch({ resurface_date: addDays(today, 30) })}>
                    +1mo
                  </button>
                  <span className="dropdown-anchor" ref={resurfacePickRef}>
                    <button className="chip" onClick={() => setResurfacePickOpen((o) => !o)}>
                      Pick date
                    </button>
                    {resurfacePickOpen && (
                      <CalendarPanel
                        value={lead.resurface_date}
                        anchorRef={resurfacePickRef}
                        onPick={(iso) => iso && patch({ resurface_date: iso })}
                        onClose={() => setResurfacePickOpen(false)}
                      />
                    )}
                  </span>
                </span>
              </div>
            )}

            <NotesBlock notes={lead.notes} onSave={(notes) => patch({ notes })} />

            <Group title="Timeline">
              <Timeline>
                {activity.map((a) => (
                  <TimelineRow key={a.id} date={tlDate(a.created_at)}>
                    {a.summary}
                  </TimelineRow>
                ))}
                <TimelineRow date={tlDate(lead.created_at)}>
                  Lead created
                  {lead.source ? (
                    <>
                      {' '}
                      from <span className="hl">{LEAD_SOURCE_LABELS[lead.source]}</span>
                    </>
                  ) : null}
                </TimelineRow>
              </Timeline>
            </Group>
          </div>

          <div className="detail-rail">
            <div className="field">
              <div className="field-label">Status</div>
              <div className="dropdown-anchor">
                <span className="select-pill" onClick={() => setStatusOpen((o) => !o)}>
                  <Pill tone={statusTone(lead.status)}>{LEAD_STATUS_LABELS[lead.status]}</Pill>
                  <span className="caret">▾</span>
                </span>
                {statusOpen && (
                  <Dropdown
                    items={LEAD_STATUSES.filter((s) => s !== 'locked_in' || canLockInLead(lead)).map((s) => ({
                      value: s,
                      label: LEAD_STATUS_LABELS[s],
                      dotColor: STATUS_DOTS[s],
                    }))}
                    value={lead.status}
                    onSelect={(status) => selectDetailStatus(status, lead.status, { lock: () => setLockOpen(true), patch }, lead.project_id)}
                    onClose={() => setStatusOpen(false)}
                  />
                )}
              </div>
            </div>
            {lead.status === 'locked_in' && (
              <div className="field">
                <div className="field-label">Start date</div>
                <InlineDate value={lead.resurface_date} onSave={(resurface_date) => patch({ resurface_date })} />
                <span className="lead-field-help">Resurfaces on this date with a Convert to project nudge.</span>
              </div>
            )}
            <div className="field">
              <div className="field-label">Estimated value · ex GST</div>
              <InlineMoney cents={lead.value_cents} onSave={(value_cents) => patch({ value_cents })} />
            </div>
            <div className="field">
              <div className="field-label">Follow up</div>
              <InlineDate value={lead.followup_date} onSave={(followup_date) => patch({ followup_date })} />
            </div>
            <div className="field">
              <div className="field-label">Company</div>
              <InlineText value={lead.company} onSave={(company) => patch({ company })} />
            </div>
            <div className="field">
              <div className="field-label">Contact</div>
              <InlineText value={lead.contact_name} onSave={(contact_name) => patch({ contact_name })} />
              <div style={{ marginTop: 2 }}>
                <InlineText
                  value={lead.contact_email}
                  mono
                  valueClass="mono"
                  display={
                    lead.contact_email ? (
                      <span style={{ color: 'var(--accent)' }}>{lead.contact_email}</span>
                    ) : undefined
                  }
                  onSave={(contact_email) => patch({ contact_email })}
                />
              </div>
              <div style={{ marginTop: 2 }}>
                <InlineText
                  value={lead.contact_phone}
                  mono
                  valueClass="mono"
                  onSave={(contact_phone) => patch({ contact_phone })}
                />
              </div>
            </div>
            <div className="field">
              <div className="field-label">Website</div>
              <InlineText
                value={lead.website}
                mono
                valueClass="mono"
                display={
                  lead.website ? <span style={{ color: 'var(--accent)' }}>{lead.website}</span> : undefined
                }
                onSave={(website) => patch({ website })}
              />
            </div>
            <div className="field">
              <div className="field-label">Address</div>
              <InlineText value={lead.address} onSave={(address) => patch({ address })} />
            </div>
            <div className="field">
              <div className="field-label">Secondary contact name</div>
              <InlineText
                value={lead.secondary_contact_name}
                onSave={(secondary_contact_name) => patch({ secondary_contact_name })}
              />
            </div>
            <div className="field">
              <div className="field-label">Secondary contact email</div>
              <InlineText
                value={lead.secondary_contact_email}
                mono
                valueClass="mono"
                display={
                  lead.secondary_contact_email ? (
                    <span style={{ color: 'var(--accent)' }}>{lead.secondary_contact_email}</span>
                  ) : undefined
                }
                onSave={(secondary_contact_email) => patch({ secondary_contact_email })}
              />
            </div>
            <div className="field">
              <div className="field-label">Source</div>
              <InlineSelect
                items={LEAD_SOURCES.map((s) => ({ value: s, label: LEAD_SOURCE_LABELS[s] }))}
                value={lead.source}
                onSave={(source: LeadSource) => patch({ source })}
              />
            </div>
            <MarketingStrategyField lead={lead} onSave={pickStrategy} />
            <div className="field">
              <div className="field-label">Type</div>
              <InlineSelect
                items={LEAD_TYPES.map((t) => ({ value: t, label: LEAD_TYPE_LABELS[t] }))}
                value={lead.lead_type}
                onSave={(lead_type: LeadType) => patch({ lead_type })}
              />
            </div>
            <div className="field lead-rail-seg">
              <div className="field-label">Request</div>
              <Seg<RequestChoice>
                options={REQUEST_OPTIONS}
                value={lead.request_type ?? ''}
                onChange={(v) => patch({ request_type: (v || null) as LeadRequestType | null })}
              />
            </div>
            <div className="field lead-rail-seg">
              <div className="field-label">Acquisition</div>
              <Seg<AcquisitionChoice>
                options={ACQUISITION_OPTIONS}
                value={lead.acquisition ?? ''}
                onChange={(v) => patch({ acquisition: (v || null) as LeadAcquisition | null })}
              />
              <div className="lead-sub-field">
                <span className="lead-sub-label">Campaign</span>
                <InlineText value={lead.campaign} placeholder="e.g. Spring clinic promo" label="Campaign" onSave={(campaign) => patch({ campaign })} />
              </div>
              {lead.external_source_ref && lead.acquisition === 'paid' && (
                <span className="lead-field-help">Tagged Paid automatically by the lead webhook.</span>
              )}
            </div>
            <div className="field">
              <div className="field-label">Enquiry date</div>
              <InlineDate value={lead.enquiry_date} onSave={(enquiry_date) => patch({ enquiry_date })} />
            </div>
            {CLOSED_DATE_LABELS[lead.status] && (
              <div className="field">
                <div className="field-label">{CLOSED_DATE_LABELS[lead.status]}</div>
                <InlineDate value={lead.closed_date} onSave={(closed_date) => patch({ closed_date })} />
              </div>
            )}
            <div className="field">
              <div className="field-label">Linked client</div>
              <div className="field-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="dropdown-anchor" style={{ minWidth: 0 }}>
                  <span className="select-pill" onClick={openClientPicker} style={{ maxWidth: '100%' }}>
                    <span>{lead.client_name ?? '— No client'}</span>
                    <span className="caret">▾</span>
                  </span>
                  {clientOpen && (
                    <Dropdown
                      items={[
                        { value: NO_CLIENT_VALUE, label: '— No client' },
                        ...(clients ?? []).map((client) => ({ value: client.id, label: client.name })),
                      ]}
                      value={lead.client_id ?? NO_CLIENT_VALUE}
                      onSelect={(clientId) => {
                        if (clientId === NO_CLIENT_VALUE) {
                          if (lead.client_id !== null) {
                            void patch({ client_id: null }, { client_id: null, client_name: null });
                          }
                        } else if (clientId !== lead.client_id) {
                          const clientName = clients?.find((client) => client.id === clientId)?.name ?? null;
                          void patch(
                            { client_id: clientId },
                            { client_id: clientId, client_name: clientName },
                          );
                        }
                      }}
                      onClose={() => setClientOpen(false)}
                      searchable
                    />
                  )}
                </div>
                {lead.client_id && (
                  <Link to={`/clients/${lead.client_id}`} style={{ color: 'var(--accent)', fontSize: 10 }}>
                    open ↗
                  </Link>
                )}
              </div>
            </div>
            {lead.project_id && (
              <div className="field">
                <div className="field-label">Converted to</div>
                <div className="field-value">
                  <Link to={`/projects/${lead.project_id}`} style={{ color: 'var(--accent)' }}>
                    {lead.converted_project_number !== null
                      ? `${formatProjectNumber(lead.converted_project_number)} `
                      : ''}
                    {lead.converted_project_name ?? lead.name} →
                  </Link>
                </div>
              </div>
            )}
            <div className="field">
              <div className="field-label">Proposals & quotes</div>
              <DocumentSendsRail
                filter={{ lead_id: lead.id }}
                defaults={{ lead_id: lead.id, client_id: lead.client_id ?? undefined, kind: 'quote' }}
              />
            </div>
            <div className="meta-stamp">
              CREATED {formatDate(melbourneDate(lead.created_at)).toUpperCase()}
              <br />
              UPDATED {formatDate(melbourneDate(lead.updated_at)).toUpperCase()}
            </div>
            <TrashAction
              label="Move lead to trash"
              onConfirm={async () => {
                await api(`/api/admin/leads/${lead.id}`, { method: 'DELETE' });
                navigate('/leads');
              }}
            />
          </div>
        </div>
      </div>

      {showConvert && (
        <ConvertLeadModal
          lead={lead}
          onClose={() => setShowConvert(false)}
          onConverted={(projectId) => navigate(`/projects/${projectId}`)}
        />
      )}
      {lockOpen && <LeadStartDateModal lead={lead} onClose={() => setLockOpen(false)} onLocked={(resurface_date) => {
        setLead((l) => (l ? { ...l, status: 'locked_in', resurface_date } : l));
        setLockOpen(false);
        refresh();
      }} />}
    </>
  );
}

// ── Convert modal (09-convert-lead) ──────────────────────────────────────

export function ConvertLeadModal({
  lead,
  onClose,
  onConverted,
}: {
  lead: LeadWithNames;
  onClose: () => void;
  onConverted: (projectId: string) => void;
}) {
  const isMobile = useIsMobile();
  const [clients, setClients] = useState<ClientWithCounts[]>([]);
  const [templates, setTemplates] = useState<{ id: string; name: string; items: unknown[] }[]>([]);
  const [mode, setMode] = useState<'new' | 'existing'>(lead.client_id ? 'existing' : 'new');
  const [form, setForm] = useState({
    name: lead.name,
    brand: 'ss' as Brand,
    project_number: '',
    project_number_source: 'pending' as 'pending' | 'suggested' | 'edited',
    client_name: lead.company ?? lead.contact_name ?? lead.name,
    client_id: lead.client_id,
    phase: 'lead' as ProjectPhase,
    value: lead.value_cents === null ? '' : formatMoney(lead.value_cents),
    due_date: null as string | null,
    // A lead already marked won keeps its date; everything else defaults to today.
    won_date: (lead.status === 'won' && lead.closed_date ? lead.closed_date : melbourneToday()) as string | null,
    copy_notes: 'copy' as 'copy' | 'leave',
    invoice: 'create' as 'create' | 'skip',
    template_id: null as string | null,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ clients: ClientWithCounts[] }>('/api/admin/clients')
      .then((d) => setClients(d.clients))
      .catch(() => {});
    api<{ next: number | null }>('/api/admin/projects/next-number')
      .then((d) => {
        setForm((current) => (
          current.project_number_source === 'pending'
            ? {
                ...current,
                project_number: d.next === null ? '' : String(d.next),
                project_number_source: 'suggested',
              }
            : current
        ));
      })
      .catch(() => {});
    api<{ templates: { id: string; name: string; items: unknown[] }[] }>('/api/admin/task-templates')
      .then((d) => setTemplates(d.templates))
      .catch(() => {});
  }, []);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.name.trim() || busy) return;
    if (mode === 'existing' && !form.client_id) {
      setError('Pick a client to link.');
      return;
    }
    const projectNumber = parseProjectNumberDraft(form.project_number);
    if (projectNumber.error) {
      setError(projectNumber.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ project_id: string }>(`/api/admin/leads/${lead.id}/convert`, {
        method: 'POST',
        json: {
          name: form.name.trim(),
          brand: form.brand,
          ...(form.project_number_source === 'pending'
            ? {}
            : { project_number: projectNumber.value }),
          ...(mode === 'existing'
            ? { client_id: form.client_id }
            : { client_name: form.client_name.trim() || null }),
          phase: form.phase,
          value_cents: parseMoney(form.value),
          due_date: form.due_date,
          won_date: form.won_date,
          copy_notes: form.copy_notes === 'copy',
          create_invoice: form.invoice === 'create',
        },
      });
      if (form.template_id) {
        await api(`/api/admin/task-templates/${form.template_id}/apply`, {
          method: 'POST',
          json: { project_id: res.project_id },
        }).catch((error) => reportError(error, 'Project created, but its task template could not be applied.'));
      }
      onConverted(res.project_id);
    } catch (err) {
      setError(projectNumberRequestError(err, 'Convert failed.'));
      setBusy(false);
    }
  };

  const activePhases: ProjectPhase[] = ['lead', 'pre-production', 'shoot', 'post'];

  // Lead values are ex GST — the draft invoice goes out at value + 10%.
  const exValue = parseMoney(form.value);
  const projectNumber = parseProjectNumberDraft(form.project_number).value;
  const draftLabel =
    exValue !== null && exValue > 0
      ? `Create draft${projectNumber !== null ? ` ${formatProjectNumber(projectNumber)}` : ''} at ${formatMoney(grossFromEx(exValue))} incl. GST`
      : `Create blank draft${projectNumber !== null ? ` ${formatProjectNumber(projectNumber)}` : ''} — fill in before sending`;

  return (
    <Modal
      mobile="full"
      title="Convert to project"
      context={lead.name.toUpperCase()}
      onClose={onClose}
      hint="Lead is marked Won and linked to the project"
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => submit()} disabled={!form.name.trim() || busy}>
            Convert →
          </button>
        </>
      }
    >
      <form className={isMobile ? "form-grid lead-convert-form" : "form-grid"} onSubmit={submit}>
        <div className="f-field span-2">
          <span className="f-label">
            Project name <span className="req">*</span>
          </span>
          <input
            className="f-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            autoFocus
          />
        </div>
        <div className="f-field span-2">
          <span className="f-label">Client</span>
          <div style={{ marginBottom: 6 }}>
            <Seg
              options={[
                { value: 'new', label: 'Create new' },
                { value: 'existing', label: 'Link existing' },
              ]}
              value={mode}
              onChange={setMode}
            />
          </div>
          {mode === 'new' ? (
            <>
              <input
                className="f-input"
                value={form.client_name}
                onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
              />
              <span className="f-help">Contact, email and phone carry over from the lead</span>
            </>
          ) : (
            <SelectField label="Client"
              items={clients.map((c) => ({ value: c.id, label: c.name }))}
              value={form.client_id}
              onChange={(client_id) => setForm((f) => ({ ...f, client_id }))}
              placeholder="Pick a client…"
              searchable
            />
          )}
        </div>
        <div className={isMobile ? "f-field lead-pair" : "f-field"}>
          <span className="f-label">Phase</span>
          <SelectField label="Phase"
            items={activePhases.map((p) => ({ value: p, label: PHASE_LABELS[p] }))}
            value={form.phase}
            onChange={(phase) => setForm((f) => ({ ...f, phase }))}
          />
        </div>
        <div className={isMobile ? "f-field lead-pair" : "f-field"}>
          <span className="f-label">Brand</span>
          <Seg<Brand>
            options={[
              { value: 'hsp', label: 'HSP' },
              { value: 'ss', label: 'SS' },
            ]}
            value={form.brand}
            onChange={(brand) => setForm((f) => ({ ...f, brand }))}
          />
        </div>
        <div className={isMobile ? "f-field lead-number" : "f-field"}>
          <span className="f-label">Project number</span>
          <input
            className="f-input mono"
            type="number"
            min={1}
            step={1}
            value={form.project_number}
            onChange={(e) => setForm((f) => ({
              ...f,
              project_number: e.target.value,
              project_number_source: 'edited',
            }))}
            placeholder="Unnumbered"
          />
          {projectNumber !== null && (
            <span className="f-help">Displays as {formatProjectNumber(projectNumber)}</span>
          )}
        </div>
        <div className={isMobile ? "f-field span-2 lead-invoice" : "f-field span-2"}>
          <span className="f-label">Invoice</span>
          <SelectField label="Invoice"
            items={[
              { value: 'create', label: draftLabel },
              { value: 'skip', label: "Don't create an invoice yet" },
            ]}
            value={form.invoice}
            onChange={(invoice) => setForm((f) => ({ ...f, invoice }))}
          />
        </div>
        <div className={isMobile ? "f-field lead-value" : "f-field"}>
          <span className="f-label">Value · ex GST</span>
          <input
            className="f-input money"
            value={form.value}
            onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
            placeholder="$0"
          />
        </div>
        <div className={isMobile ? "f-field lead-due" : "f-field"}>
          <span className="f-label">Due date</span>
          <DateField label="Due date" value={form.due_date} onChange={(due_date) => setForm((f) => ({ ...f, due_date }))} />
        </div>
        <div className={isMobile ? "f-field lead-won" : "f-field"}>
          <span className="f-label">Won on</span>
          <DateField label="Won on" value={form.won_date} onChange={(won_date) => setForm((f) => ({ ...f, won_date }))} />
          <span className="f-help">Defaults to today · sets the lead's won date</span>
        </div>
        <div className={isMobile ? "f-field lead-notes" : "f-field"}>
          <span className="f-label">Lead notes</span>
          {isMobile ? <Seg options={[{ value: 'copy', label: 'Copy to project' }, { value: 'leave', label: 'Leave on lead' }]} value={form.copy_notes} onChange={(copy_notes) => setForm(f => ({ ...f, copy_notes }))} /> : <SelectField
            items={[
              { value: 'copy', label: 'Copy to project' },
              { value: 'leave', label: 'Leave on lead' },
            ]}
            value={form.copy_notes}
            onChange={(copy_notes) => setForm((f) => ({ ...f, copy_notes }))}
          />}
        </div>
        {(isMobile || templates.length > 0) && (
          <div className="f-field">
            <span className="f-label">Tasks</span>
            <SelectField label="Tasks"
              items={[
                { value: '', label: 'No template' },
                ...templates.map((t) => ({ value: t.id, label: `${t.name} · ${t.items.length}` })),
              ]}
              value={form.template_id ?? ''}
              onChange={(template_id) => setForm((f) => ({ ...f, template_id: template_id || null }))}
            />
          </div>
        )}
        {isMobile && <p className="lead-carry-note">Contact, email and phone carry over from the lead.</p>}
        {error && <p className="error-text span-2">{error}</p>}
      </form>
    </Modal>
  );
}
